import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { hostUI } from '../../../../configuraciones';

import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return '';
  const num = Number(bytes);
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? String(dateStr) : d.toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch (_) {
    return String(dateStr);
  }
};

export const TIPOS_DOCUMENTO_RANGO = [
  {
    id: 'certificado_producto_envasado',
    nombre: 'Certificado de Producto Envasado',
    desc: 'Certificado oficial NOM-070 de producto envasado para este rango (puedes subir varios)',
    icon: PictureAsPdfIcon,
    color: '#16A34A',
    bg: '#F0FDF4',
    border: '#BBF7D0'
  },
  {
    id: 'informe_servicio',
    nombre: 'Informe de Servicio',
    desc: 'Informe o acta de verificación / envasado emitido por UI (puedes subir varios)',
    icon: AssignmentIcon,
    color: '#7C3AED',
    bg: '#FAF5FF',
    border: '#E9D5FF'
  },
  {
    id: 'otro',
    nombre: 'Otro Documento',
    desc: 'Constancias, aprobaciones de etiqueta o anexos complementarios (puedes subir varios)',
    icon: FolderSharedIcon,
    color: '#D97706',
    bg: '#FFFBEB',
    border: '#FDE68A'
  }
];

/**
 * Sube automáticamente los archivos en cola (`documentosPendientes`) de cada rango
 * una vez que el lote / rangos fueron guardados en base de datos y ya cuentan con `id_producto_holograma`.
 */
export const subirDocumentosPendientesDeRangos = async (rangosDraft = [], loteGuardado = null, idLogin = null) => {
  if (!Array.isArray(rangosDraft) || rangosDraft.length === 0 || !loteGuardado) return;
  const rangosBd = loteGuardado.rangosHologramas || loteGuardado.rangos_hologramas || [];
  if (!Array.isArray(rangosBd) || rangosBd.length === 0) return;

  const normNum = (v) => String(v || '').replace(/\D/g, '');

  for (const draft of rangosDraft) {
    const pendientes = Array.isArray(draft.documentosPendientes) ? draft.documentosPendientes : [];
    if (pendientes.length === 0) continue;

    // Localizar el rango correspondiente en la respuesta de BD
    let targetId = Number(draft.id_producto_holograma);
    if (!targetId || isNaN(targetId)) {
      const prefDraft = String(draft.prefijo || 'A').trim().toUpperCase();
      const iniDraft = normNum(draft.rango1 || draft.rango_inicial);
      const finDraft = normNum(draft.rango2 || draft.rango_final);
      const match = rangosBd.find((rb) => {
        const prefBd = String(rb.prefijo || 'A').trim().toUpperCase();
        const iniBd = normNum(rb.rango1 || rb.rango_inicial);
        const finBd = normNum(rb.rango2 || rb.rango_final);
        return prefBd === prefDraft && iniBd === iniDraft && finBd === finDraft;
      });
      if (match) {
        targetId = Number(match.id_producto_holograma || match.id);
      }
    }

    if (!targetId || isNaN(targetId)) continue;

    for (const item of pendientes) {
      if (!item?.file) continue;
      try {
        const formData = new FormData();
        formData.append('archivo', item.file);
        formData.append('tipo_documento', item.tipo_documento || 'certificado_producto_envasado');
        formData.append(
          'nombre_documento',
          item.tipo_documento === 'otro'
            ? (item.nombre_documento || item.file.name)
            : (TIPOS_DOCUMENTO_RANGO.find((t) => t.id === item.tipo_documento)?.nombre || 'Certificado de Producto Envasado')
        );
        if (item.observaciones) formData.append('observaciones', item.observaciones);
        if (idLogin) formData.append('id_login', idLogin);
        formData.append('origen_documento', item.origen_documento || 'envasado_no_terminado');

        await axios.post(`${hostUI}bitacora-envasado/producto/rango/${targetId}/documentos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) {
        console.warn('Error al subir documento pendiente del rango:', err);
      }
    }
  }
};

/**
 * Sube automáticamente los archivos en cola (`documentosPendientes`) de cada SALIDA
 * una vez que el lote / rangos / salidas fueron guardados en base de datos y ya cuentan con `id_salida`.
 */
export const subirDocumentosPendientesDeSalidas = async (rangosDraft = [], loteGuardado = null, idLogin = null) => {
  if (!Array.isArray(rangosDraft) || rangosDraft.length === 0 || !loteGuardado) return;
  const rangosBd = loteGuardado.rangosHologramas || loteGuardado.rangos_hologramas || [];
  if (!Array.isArray(rangosBd) || rangosBd.length === 0) return;

  const normNum = (v) => String(v || '').replace(/\D/g, '');

  for (const draftRango of rangosDraft) {
    const salidasDraft = Array.isArray(draftRango.salidas) ? draftRango.salidas : [];
    if (salidasDraft.length === 0) continue;

    // Localizar el rango en BD
    const prefDraft = String(draftRango.prefijo || 'A').trim().toUpperCase();
    const iniDraft = normNum(draftRango.rango1 || draftRango.rango_inicial);
    const finDraft = normNum(draftRango.rango2 || draftRango.rango_final);
    const rangoBd = rangosBd.find((rb) => {
      if (draftRango.id_producto_holograma && Number(rb.id_producto_holograma || rb.id) === Number(draftRango.id_producto_holograma)) {
        return true;
      }
      const prefBd = String(rb.prefijo || 'A').trim().toUpperCase();
      const iniBd = normNum(rb.rango1 || rb.rango_inicial);
      const finBd = normNum(rb.rango2 || rb.rango_final);
      return prefBd === prefDraft && iniBd === iniDraft && finBd === finDraft;
    });

    const salidasBd = Array.isArray(rangoBd?.salidas) ? rangoBd.salidas : [];

    for (const sDraft of salidasDraft) {
      const pendientes = Array.isArray(sDraft.documentosPendientes) ? sDraft.documentosPendientes : [];
      if (pendientes.length === 0) continue;

      let targetSalidaId = Number(sDraft.id_salida || sDraft.id);
      if (!targetSalidaId || isNaN(targetSalidaId)) {
        const sIni = normNum(sDraft.rango1 || sDraft.rango_inicial);
        const sFin = normNum(sDraft.rango2 || sDraft.rango_final);
        const matchSal = salidasBd.find((sb) => {
          const sbIni = normNum(sb.rango1 || sb.rango_inicial);
          const sbFin = normNum(sb.rango2 || sb.rango_final);
          return sbIni === sIni && sbFin === sFin;
        });
        if (matchSal) {
          targetSalidaId = Number(matchSal.id_salida || matchSal.id);
        }
      }

      if (!targetSalidaId || isNaN(targetSalidaId)) continue;

      for (const item of pendientes) {
        if (!item?.file) continue;
        try {
          const formData = new FormData();
          formData.append('archivo', item.file);
          formData.append('tipo_documento', item.tipo_documento || 'certificado_producto_envasado');
          formData.append(
            'nombre_documento',
            item.tipo_documento === 'otro'
              ? (item.nombre_documento || item.file.name)
              : (TIPOS_DOCUMENTO_RANGO.find((t) => t.id === item.tipo_documento)?.nombre || 'Certificado de Producto Envasado')
          );
          if (item.observaciones) formData.append('observaciones', item.observaciones);
          if (idLogin) formData.append('id_login', idLogin);
          formData.append('origen_documento', item.origen_documento || 'salida_holograma');

          await axios.post(`${hostUI}bitacora-envasado/producto/salida/${targetSalidaId}/documentos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) {
          console.warn('Error al subir documento pendiente de la salida:', err);
        }
      }
    }
  }
};

const ModalDocumentosRangoHolograma = ({
  isOpen,
  onClose,
  rango,
  lote = null,
  rangosDelLote = [],
  onDocumentUpdated
}) => {
  const [selectedRango, setSelectedRango] = useState(rango || null);
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [activeTab, setActiveTab] = useState('listado'); // 'listado' | 'nuevo'

  // Formulario nuevo documento
  const [tipoDocumento, setTipoDocumento] = useState('certificado_producto_envasado');
  const [nombreDocumento, setNombreDocumento] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [archivosSeleccionados, setArchivosSeleccionados] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (rango) {
        setSelectedRango(rango);
      } else if (Array.isArray(rangosDelLote) && rangosDelLote.length > 0) {
        setSelectedRango(rangosDelLote[0]);
      }
    }
  }, [isOpen, rango, rangosDelLote]);

  const idRango = Number(selectedRango?.id_producto_holograma || selectedRango?.id);
  const esRangoPersistido = Number.isInteger(idRango) && idRango > 0;

  const etiquetaRango = selectedRango
    ? (selectedRango.rangoHologramas || selectedRango.rango_completo || `${selectedRango.prefijo || 'A'}${selectedRango.rango1 || selectedRango.rango_inicial || ''} - ${selectedRango.prefijo || 'A'}${selectedRango.rango2 || selectedRango.rango_final || ''}`)
    : 'Sin rango seleccionado';

  const fetchDocumentos = async () => {
    if (!esRangoPersistido) {
      setDocumentos([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${hostUI}bitacora-envasado/producto/rango/${idRango}/documentos`);
      if (res.data?.data) {
        setDocumentos(res.data.data);
      } else {
        setDocumentos([]);
      }
    } catch (error) {
      console.error('Error al obtener documentos del rango de hologramas:', error);
      setDocumentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && esRangoPersistido) {
      fetchDocumentos();
      setActiveTab('listado');
      resetFormulario();
    }
  }, [isOpen, idRango]);

  const resetFormulario = () => {
    setTipoDocumento('certificado_producto_envasado');
    setNombreDocumento('');
    setObservaciones('');
    setArchivosSeleccionados([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedExts = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
    const validFiles = [];

    for (const file of files) {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowedExts.includes(ext)) {
        Swal.fire({
          icon: 'warning',
          title: 'Formato no válido',
          text: `El archivo "${file.name}" no es válido. Solo se permiten PDF o imágenes (JPG, PNG, WEBP).`,
          confirmButtonColor: '#6B21A8'
        });
        continue;
      }
      if (file.size > 30 * 1024 * 1024) {
        Swal.fire({
          icon: 'warning',
          title: 'Archivo demasiado grande',
          text: `El archivo "${file.name}" supera el límite de 30 MB.`,
          confirmButtonColor: '#6B21A8'
        });
        continue;
      }
      validFiles.push(file);
    }

    setArchivosSeleccionados((prev) => [...prev, ...validFiles]);
  };

  const handleRemoveSelectedFile = (idx) => {
    setArchivosSeleccionados((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubirDocumento = async (e) => {
    e.preventDefault();
    if (!esRangoPersistido) {
      Swal.fire({
        icon: 'info',
        title: 'Rango aún no guardado',
        text: 'Primero guarde el rango de hologramas para subir documentos al expediente en el servidor.',
        confirmButtonColor: '#6B21A8'
      });
      return;
    }

    if (archivosSeleccionados.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo requerido',
        text: 'Por favor selecciona al menos un archivo PDF o imagen para adjuntar al rango de hologramas.',
        confirmButtonColor: '#6B21A8'
      });
      return;
    }

    if (tipoDocumento === 'otro' && !nombreDocumento.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Nombre requerido',
        text: 'Por favor especifica el nombre o descripción para "Otro Documento".',
        confirmButtonColor: '#6B21A8'
      });
      return;
    }

    const tipoObj = TIPOS_DOCUMENTO_RANGO.find((t) => t.id === tipoDocumento);
    const nombreFinalBase = tipoDocumento === 'otro'
      ? nombreDocumento.trim()
      : (tipoObj?.nombre || 'Certificado de Producto Envasado');

    setSubiendo(true);
    try {
      for (let i = 0; i < archivosSeleccionados.length; i++) {
        const file = archivosSeleccionados[i];
        const formData = new FormData();
        formData.append('archivo', file);
        formData.append('tipo_documento', tipoDocumento);
        const nombreDoc = archivosSeleccionados.length > 1 && tipoDocumento === 'otro'
          ? `${nombreFinalBase} (${i + 1})`
          : nombreFinalBase;
        formData.append('nombre_documento', nombreDoc);
        if (observaciones.trim()) {
          formData.append('observaciones', observaciones.trim());
        }

        await axios.post(
          `${hostUI}bitacora-envasado/producto/rango/${idRango}/documentos`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      Swal.fire({
        icon: 'success',
        title: archivosSeleccionados.length > 1 ? '¡Documentos adjuntados!' : '¡Documento adjuntado!',
        text: `Se ${archivosSeleccionados.length > 1 ? `guardaron ${archivosSeleccionados.length} documentos` : `guardó "${nombreFinalBase}"`} en el expediente del rango ${etiquetaRango}.`,
        timer: 2000,
        showConfirmButton: false
      });

      resetFormulario();
      setActiveTab('listado');
      await fetchDocumentos();
      if (onDocumentUpdated) onDocumentUpdated();
    } catch (error) {
      console.error('Error al subir documento del rango:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al subir documento',
        text: error.response?.data?.message || 'Ocurrió un problema al guardar el archivo.',
        confirmButtonColor: '#DC2626'
      });
    } finally {
      setSubiendo(false);
    }
  };

  const handleVerDocumento = (doc) => {
    const url = `${hostUI}bitacora-envasado/producto/rango/documentos/${doc.id_documento}/ver`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleEliminarDocumento = async (doc) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar documento?',
      html: `Se eliminará permanentemente <b>${doc.nombre_documento}</b> (${doc.archivo_nombre_original || doc.nombre_original || 'archivo'}) del rango <b>${etiquetaRango}</b>.`,
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#64748B',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${hostUI}bitacora-envasado/producto/rango/documentos/${doc.id_documento}`);
      Swal.fire({
        icon: 'success',
        title: 'Eliminado',
        text: 'El documento fue eliminado del rango.',
        timer: 1600,
        showConfirmButton: false
      });
      await fetchDocumentos();
      if (onDocumentUpdated) onDocumentUpdated();
    } catch (error) {
      console.error('Error al eliminar documento del rango:', error);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo eliminar',
        text: error.response?.data?.message || 'Error al eliminar el documento.',
        confirmButtonColor: '#DC2626'
      });
    }
  };

  const getBadgeForTipo = (tipo) => {
    const found = TIPOS_DOCUMENTO_RANGO.find((t) => t.id === tipo) || TIPOS_DOCUMENTO_RANGO[2];
    const IconComp = found.icon;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '4px 10px',
          borderRadius: '999px',
          fontSize: '11px',
          fontWeight: 700,
          backgroundColor: found.bg,
          color: found.color,
          border: `1px solid ${found.border}`
        }}
      >
        <IconComp style={{ fontSize: 13 }} />
        {found.nombre}
      </span>
    );
  };

  const conteoPorTipo = TIPOS_DOCUMENTO_RANGO.reduce((acc, t) => {
    acc[t.id] = documentos.filter((d) => d.tipo_documento === t.id).length;
    return acc;
  }, {});

  const listaRangos = Array.isArray(rangosDelLote) && rangosDelLote.length > 0
    ? rangosDelLote
    : (selectedRango ? [selectedRango] : []);

  const modalContent = (
    <div className="modal-backdrop-custom animate-fadeIn" style={{ zIndex: 1100000 }} onClick={onClose}>
      <div
        className="detail-modal-card animate-fadeIn"
        style={{
          maxWidth: '880px',
          width: '95%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.35)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div
          className="detail-modal-header"
          style={{
            background: 'linear-gradient(135deg, #581C87 0%, #6B21A8 60%, #7E22CE 100%)',
            color: '#fff',
            padding: '18px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <span
                style={{
                  background: 'rgba(255,255,255,0.18)',
                  color: '#F3E8FF',
                  padding: '3px 10px',
                  borderRadius: '999px',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.03em'
                }}
              >
                EXPEDIENTE POR RANGO DE HOLOGRAMAS
              </span>
              {lote?.loteEnvasado && (
                <span
                  style={{
                    background: 'rgba(255,255,255,0.14)',
                    color: '#fff',
                    padding: '3px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}
                >
                  Lote Envasado: {lote.loteEnvasado} {lote.marca ? `— ${lote.marca}` : ''}
                </span>
              )}
            </div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ConfirmationNumberIcon style={{ fontSize: 20 }} />
              <span>Documentos del Rango: {etiquetaRango}</span>
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>
              Adjunta el Certificado de Producto Envasado, Informe de Servicio u otros documentos de este rango.
            </p>
          </div>
          <button
            className="btn-close-modal"
            onClick={onClose}
            style={{ color: '#fff', background: 'rgba(255,255,255,0.15)' }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* SELECTOR DE RANGO SI EL LOTE TIENE VARIOS RANGOS */}
        {listaRangos.length > 1 && (
          <div
            style={{
              padding: '10px 24px',
              background: '#FAF5FF',
              borderBottom: '1px solid #E9D5FF',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#6B21A8' }}>
              Seleccionar Rango del Lote:
            </span>
            {listaRangos.map((rg, idx) => {
              const rgId = rg.id_producto_holograma || rg.id;
              const isSelected = String(rgId) === String(idRango);
              const txt = rg.rangoHologramas || rg.rango_completo || `${rg.prefijo || 'A'}${rg.rango1 || rg.rango_inicial} - ${rg.prefijo || 'A'}${rg.rango2 || rg.rango_final}`;
              const docsCount = Array.isArray(rg.documentos) ? rg.documentos.length : (rg.total_documentos || 0);
              return (
                <button
                  key={rgId || idx}
                  type="button"
                  onClick={() => setSelectedRango(rg)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #6B21A8' : '1px solid #D8B4FE',
                    background: isSelected ? '#6B21A8' : '#fff',
                    color: isSelected ? '#fff' : '#6B21A8',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span>Rango #{idx + 1}: {txt}</span>
                  <span
                    style={{
                      background: isSelected ? 'rgba(255,255,255,0.25)' : '#F3E8FF',
                      color: isSelected ? '#fff' : '#6B21A8',
                      padding: '1px 6px',
                      borderRadius: '999px',
                      fontSize: '10px'
                    }}
                  >
                    {docsCount} doc{docsCount === 1 ? '' : 's'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* BARRA DE PESTAÑAS */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 24px',
            backgroundColor: '#F8FAFC',
            borderBottom: '1px solid #E2E8F0',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setActiveTab('listado')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: activeTab === 'listado' ? '1.5px solid #6B21A8' : '1px solid #CBD5E1',
                backgroundColor: activeTab === 'listado' ? '#FAF5FF' : '#fff',
                color: activeTab === 'listado' ? '#6B21A8' : '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <FolderSharedIcon style={{ fontSize: 16 }} />
              Documentos del Rango ({documentos.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('nuevo')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: activeTab === 'nuevo' ? '1.5px solid #6B21A8' : '1px solid #CBD5E1',
                backgroundColor: activeTab === 'nuevo' ? '#6B21A8' : '#fff',
                color: activeTab === 'nuevo' ? '#fff' : '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <AddCircleOutlineIcon style={{ fontSize: 16 }} />
              Adjuntar Documento al Rango
            </button>
          </div>

          <button
            type="button"
            onClick={fetchDocumentos}
            disabled={loading}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#fff',
              color: '#475569',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <RefreshIcon style={{ fontSize: 15 }} />
            Actualizar
          </button>
        </div>

        {/* BODY */}
        <div
          className="detail-modal-body"
          style={{
            padding: '20px 24px',
            overflowY: 'auto',
            flex: 1,
            backgroundColor: '#fff'
          }}
        >
          {/* Tarjetas resumen por categoría */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))',
              gap: '10px',
              marginBottom: '18px'
            }}
          >
            {TIPOS_DOCUMENTO_RANGO.map((t) => {
              const IconComp = t.icon;
              const count = conteoPorTipo[t.id] || 0;
              return (
                <div
                  key={t.id}
                  onClick={() => {
                    setTipoDocumento(t.id);
                    setActiveTab('nuevo');
                  }}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: `1.5px solid ${count > 0 ? t.border : '#E2E8F0'}`,
                    backgroundColor: count > 0 ? t.bg : '#F8FAFC',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  title={`Clic para adjuntar ${t.nombre}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: t.color,
                        border: `1px solid ${t.border}`
                      }}
                    >
                      <IconComp style={{ fontSize: 18 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
                        {t.nombre}
                      </div>
                      <div style={{ fontSize: '10.5px', color: count > 0 ? t.color : '#64748B', fontWeight: 600 }}>
                        {count > 0 ? `${count} archivo(s)` : 'Sin adjuntar'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {activeTab === 'listado' ? (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748B' }}>
                  Cargando documentos del rango {etiquetaRango}...
                </div>
              ) : documentos.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '36px 20px',
                    borderRadius: '12px',
                    border: '2px dashed #CBD5E1',
                    backgroundColor: '#F8FAFC'
                  }}
                >
                  <UploadFileIcon style={{ fontSize: 44, color: '#94A3B8', marginBottom: 8 }} />
                  <h4 style={{ margin: '0 0 6px', color: '#334155', fontSize: '15px', fontWeight: 700 }}>
                    Este rango de hologramas aún no tiene documentos cargados
                  </h4>
                  <p style={{ margin: '0 0 16px', color: '#64748B', fontSize: '13px' }}>
                    Puedes adjuntar el <b>Certificado de Producto Envasado</b>, el <b>Informe de Servicio</b> u otros documentos.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab('nuevo')}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#6B21A8',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <AddCircleOutlineIcon style={{ fontSize: 17 }} />
                    Adjuntar Primer Documento
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {documentos.map((doc) => (
                    <div
                      key={doc.id_documento}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: '240px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                          {getBadgeForTipo(doc.tipo_documento)}
                          <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#0F172A' }}>
                            {doc.nombre_documento}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748B', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <span>📎 {doc.archivo_nombre_original || doc.nombre_original || doc.nombre_archivo}</span>
                          {doc.tamano_bytes ? <span>• {formatFileSize(doc.tamano_bytes)}</span> : null}
                          {doc.createdAt ? <span>• Subido: {formatDate(doc.createdAt)}</span> : null}
                        </div>
                        {doc.observaciones && (
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#475569', fontStyle: 'italic' }}>
                            "{doc.observaciones}"
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => handleVerDocumento(doc)}
                          style={{
                            padding: '7px 13px',
                            borderRadius: '8px',
                            border: '1px solid #C084FC',
                            backgroundColor: '#FAF5FF',
                            color: '#6B21A8',
                            fontWeight: 700,
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                        >
                          <VisibilityIcon style={{ fontSize: 15 }} />
                          Ver / Descargar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEliminarDocumento(doc)}
                          style={{
                            padding: '7px 10px',
                            borderRadius: '8px',
                            border: '1px solid #FECACA',
                            backgroundColor: '#FEF2F2',
                            color: '#DC2626',
                            fontWeight: 700,
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title="Eliminar documento"
                        >
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubirDocumento} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1E293B', marginBottom: '8px' }}>
                  1. Selecciona el Tipo de Documento *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px' }}>
                  {TIPOS_DOCUMENTO_RANGO.map((t) => {
                    const IconComp = t.icon;
                    const selected = tipoDocumento === t.id;
                    return (
                      <div
                        key={t.id}
                        onClick={() => setTipoDocumento(t.id)}
                        style={{
                          padding: '12px',
                          borderRadius: '10px',
                          border: selected ? `2px solid ${t.color}` : '1px solid #CBD5E1',
                          backgroundColor: selected ? t.bg : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <IconComp style={{ fontSize: 18, color: t.color }} />
                          <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#0F172A' }}>
                            {t.nombre}
                          </span>
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748B', lineHeight: 1.35 }}>
                          {t.desc}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {tipoDocumento === 'otro' && (
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1E293B', marginBottom: '6px' }}>
                    Nombre del Documento *
                  </label>
                  <input
                    type="text"
                    value={nombreDocumento}
                    onChange={(e) => setNombreDocumento(e.target.value)}
                    placeholder="Ej. Dictamen de Etiqueta, Constancia de Exportación, etc."
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #CBD5E1',
                      fontSize: '13px'
                    }}
                  />
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1E293B', marginBottom: '6px' }}>
                  2. Selecciona Archivo(s) (PDF, JPG, PNG — Máx. 30 MB) *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  multiple
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1.5px dashed #9333EA',
                    backgroundColor: '#FAF5FF',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                />
                {archivosSeleccionados.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {archivosSeleccionados.map((f, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 10px',
                          backgroundColor: '#F3E8FF',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#581C87'
                        }}
                      >
                        <span>📄 {f.name} ({formatFileSize(f.size)})</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSelectedFile(i)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#DC2626',
                            cursor: 'pointer',
                            fontWeight: 700
                          }}
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1E293B', marginBottom: '6px' }}>
                  3. Observaciones (Opcional)
                </label>
                <textarea
                  rows={2}
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Notas adicionales sobre el certificado o informe de este rango..."
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    fontSize: '13px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    resetFormulario();
                    setActiveTab('listado');
                  }}
                  style={{
                    padding: '9px 16px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    backgroundColor: '#fff',
                    color: '#475569',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={subiendo}
                  style={{
                    padding: '9px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: subiendo ? 'not-allowed' : 'pointer',
                    opacity: subiendo ? 0.7 : 1
                  }}
                >
                  {subiendo ? 'Subiendo archivo(s)...' : 'Guardar en Expediente del Rango'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* FOOTER */}
        <div
          className="detail-modal-footer"
          style={{
            padding: '12px 24px',
            backgroundColor: '#F8FAFC',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ fontSize: '12px', color: '#64748B' }}>
            Total de documentos en este rango: <b>{documentos.length}</b>
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 18px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#64748B',
              color: '#fff',
              fontWeight: 700,
              fontSize: '12.5px',
              cursor: 'pointer'
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
};

export default ModalDocumentosRangoHolograma;

